// src/components/EditReminder.jsx
import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { toast } from 'react-toastify';
import ConfirmationDialog from '../utils/ConfirmationDialog';

const EditReminder = ({ reminder, API, onClose, onUpdate }) => {
  const [reminderText, setReminderText] = useState(reminder.text);
  const [reminderDate, setReminderDate] = useState(new Date(reminder.reminderDate));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!reminderText.trim()) {
      toast.error('La description de la tâche est obligatoire');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await API.put(`/api/reminders/${reminder._id}`, {
        text: reminderText.trim(),
        reminderDate
      });

      if (response.data.success) {
        toast.success('Tâche mis à jour avec succès');
        onUpdate(response.data.reminder);
        onClose();
      } else {
        toast.error(response.data.message || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Error updating reminder:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la mise à jour de la tâche');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setIsSubmitting(true);
    setShowDeleteDialog(false);

    try {
      const response = await API.delete(`/api/reminders/${reminder._id}`);

      if (response.data.success) {
        toast.success('Tâche supprimée avec succès');
        onUpdate(null, reminder._id); // null pour indiquer la suppression
        onClose();
      } else {
        toast.error(response.data.message || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Error deleting reminder:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la suppression de la tâche');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-1/3 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-semibold mb-4">Modifier la tâche</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Description
            </label>
            <textarea
              value={reminderText}
              onChange={(e) => setReminderText(e.target.value)}
              className="w-full p-2 border rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              rows="4"
              required
              placeholder="Entrez le message de la tâche..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Date de rappel
            </label>
            <DatePicker
              selected={reminderDate}
              onChange={date => setReminderDate(date)}
              className="w-full p-2 border rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              minDate={new Date()}
              dateFormat="dd/MM/yyyy HH:mm"
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              timeCaption="Heure"
            />
          </div>

          <div className="flex justify-between space-x-3 pt-4 ">
            <button
              type="button"
              onClick={() => setShowDeleteDialog(true)}
              disabled={isSubmitting}
              className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Supprimer
            </button>
            
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-4 py-2 border rounded hover:bg-gray-100 disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Mise à jour...' : 'Mettre à jour'}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        title="Supprimer la tâche"
        message="Êtes-vous sûr de vouloir supprimer cette tâche ? Cette action est irréversible."
        confirmText="Supprimer"
        cancelText="Annuler"
        isDangerous={true}
      />
    </div>
  );
};

export default EditReminder;